import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { toNumber } from 'lodash';
import classNames from 'classnames';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import Button from '@atlaskit/button';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { BillEstimateOrderItemDto } from '@css-xp/common-services/src/api/gateways/ccp-gateways/src/ccp-api-order';
import { FormatCurrency, getMinimumFractionDigitsByCurrency } from '@css-xp/ppc-common';
import { TaxItems } from '@atlassian/cssxp-types-billing';
import { Currency } from '@atlassian/cssxp-ccp-types';
import { DISCOUNT_LABELS } from '@css-xp/base';
import { getProratedCharge } from '@css-xp/ppc-utils/src/entitlement-utils';

const defaultAllItems: BillEstimateOrderItemDto['items'] = [];

export type DiscountAdjustments = BillEstimateOrderItemDto['items'][0]['adjustments'];
export type discountListItem = {
  discountType: string;
  discountLabel: string;
  discountValue: number;
};

export const formatToUSD = (amount: number, currency = 'USD') => {
  // To improve: Can be moved to a common util
  return `${currency} ${amount?.toLocaleString(undefined, getMinimumFractionDigitsByCurrency(currency))}`;
};

const renderPrimitiveTooltip = (margin: string) => {
  return styled(TooltipPrimitive)`
    background: #172b4d;
    border-radius: 3px;
    color: #fff;
    padding: 2px 6px;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin-right: ${margin};
  `;
};

const PriceGridItem = ({
  label,
  value,
  helpContent,
  customClassValue = '',
  isChildItem = false,
}: {
  label: string;
  value: string;
  helpContent?: React.ReactElement | null;
  customClassValue?: string;
  isChildItem?: boolean;
}) => {
  return (
    <>
      <div
        className={classNames('price-grid-item price-grid-item__label', customClassValue, {
          'pl-8': isChildItem,
        })}
      >
        {label}
        {helpContent ? helpContent : null}
      </div>
      <div className={classNames('price-grid-item price-grid-item__value', customClassValue)}>{value}</div>
    </>
  );
};

export const PriceDifferenceContent = ({
  isLoading,
  billEstimate,
  linkedEntitlementsBillEstimate = [],
  currentEstimatedBill = 0,
  setGrandTotal,
  hideAdminFeeTooltip = true,
  shouldShowProratedCharge,
  currency,
  isDesignIteration1Enabled,
}: {
  isLoading: boolean;
  billEstimate?: BillEstimateOrderItemDto;
  linkedEntitlementsBillEstimate?: BillEstimateOrderItemDto[];
  currentEstimatedBill?: number;
  setGrandTotal?: (_value: number) => void;
  hideAdminFeeTooltip?: boolean;
  shouldShowProratedCharge: boolean;
  currency?: string;
  isDesignIteration1Enabled?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  billEstimate = billEstimate || ({} as BillEstimateOrderItemDto);
  // in case bill estimation fails, we still need the currency to show the right currency with 0 values.
  const finalCurrency = billEstimate?.currency?.toUpperCase() || currency || Currency.USD;
  const finalEstimates: BillEstimateOrderItemDto[] = useMemo(() => {
    if (Object.keys(billEstimate as BillEstimateOrderItemDto).length !== 0) {
      return [{ ...(billEstimate as BillEstimateOrderItemDto) }, ...linkedEntitlementsBillEstimate];
    }
    return [...linkedEntitlementsBillEstimate];
  }, [linkedEntitlementsBillEstimate, billEstimate]);

  const discountArrowToShow = isExpanded ? <ChevronDownIcon label="collapse" /> : <ChevronRightIcon label="expand" />;

  const adminFeeDetails = useMemo(() => {
    // The first item in the final estimates should have the current bill estimate. Check line #36.
    // Object.keys(billEstimate as BillEstimateOrderItemDto).length !== 0 signifies that bill estimate API
    // was done successfully and the object has the current estimate for the changed subscription details.
    if (finalEstimates && Object.keys(billEstimate as BillEstimateOrderItemDto).length !== 0) {
      const adminFeeAmount = finalEstimates?.reduce<number>((total, estimate) => {
        // only items where metadata is not null can be assumed to have DST details
        return (
          total +
          (estimate?.items?.filter((item) => item.metadata)?.reduce<number>((acc, item) => acc + item.subtotal, 0) ?? 0)
        );
      }, 0);
      return adminFeeAmount
        ? {
            amount: adminFeeAmount,
            percent: 2,
          }
        : undefined;
    }
    return null;
  }, [finalEstimates]);

  const finalItems = useMemo(() => {
    const finalItemsList: BillEstimateOrderItemDto['items'] = [];
    finalEstimates?.forEach((estimate: BillEstimateOrderItemDto) => {
      estimate?.items
        // only items where metadata is null can be considered as non-DST items.
        ?.filter((item) => !item.metadata)
        .forEach((item: BillEstimateOrderItemDto['items'][0]) => {
          finalItemsList.push(item);
        });
    });
    return finalItemsList;
  }, [finalEstimates]);

  const allItems = finalItems || defaultAllItems;

  const flattenAdjustments = (flattenedList: DiscountAdjustments, item: BillEstimateOrderItemDto['items'][0]) => {
    const allAdjustmentsForItem = item.adjustments;

    if (allAdjustmentsForItem) {
      flattenedList = flattenedList.concat(allAdjustmentsForItem);
    }
    return flattenedList;
  };

  const allDiscountAdjustments = useMemo(() => allItems.reduce(flattenAdjustments, []), [allItems]);

  const grandTotal = useMemo(() => {
    const result = finalEstimates.reduce((sum, currentItem) => (sum += currentItem.grandTotal), 0);
    setGrandTotal && setGrandTotal(result);
    return result;
  }, [finalEstimates]);

  const taxes = useMemo(() => finalEstimates.reduce((sum, currentItem) => (sum += currentItem.totalTax), 0), [
    finalEstimates,
  ]);

  const { credits, discount, subTotal } = useMemo(() => {
    if (isLoading || !billEstimate) {
      return {
        credits: 0,
        discount: 0,
        subTotal: 0,
      };
    }

    const creditItems = allItems.filter((item) => item.total < 0);

    const credits = creditItems.reduce((sum, currentItem) => {
      const creditAmount = currentItem.total * -1;
      sum += creditAmount;

      return sum;
    }, 0);

    const subTotalItems = allItems.filter((item) => item.subtotal > 0);
    const subTotal = subTotalItems.reduce((sum, currentItem) => (sum += currentItem.subtotal), 0);

    const discount = allDiscountAdjustments.reduce((sum, currentAdjustment) => {
      sum += currentAdjustment.amount;

      return sum;
    }, 0);

    return {
      credits,
      discount,
      subTotal,
    };
  }, [allDiscountAdjustments, allItems, billEstimate, isLoading]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (isLoading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader width="200px" />
        <br />
        <SkeletonLoader width="200px" />
      </SkeletonLoaderWrapper>
    );
  }

  interface TaxRates {
    taxLabel: string;
    tax: number;
  }

  const getTaxRates = (items: BillEstimateOrderItemDto['items'], paymentCurrency: string | null) => {
    const taxSummaryItems: TaxRates[] = [];
    items?.forEach((item: any) => {
      item?.taxItems?.forEach((taxItem: TaxItems) => {
        const taxSummaryKey = toNumber(taxItem.taxPercent) + '% ' + taxItem.taxAmountLabel;
        const existingSummaryItem = taxSummaryItems.find(
          (taxSummaryItem: TaxRates) => taxSummaryItem.taxLabel === taxSummaryKey
        );
        if (existingSummaryItem) {
          existingSummaryItem.tax += toNumber(taxItem.tax);
        } else {
          taxSummaryItems.push({
            taxLabel: taxSummaryKey,
            tax: toNumber(taxItem.tax),
          });
        }
      });
    });

    return taxSummaryItems?.length > 0 ? (
      <>
        {taxSummaryItems.map((item: TaxRates, index: number) => {
          return (
            <div className="tax-value" key={index}>
              {item.taxLabel}:{' '}
              <FormatCurrency showCommas showCurrencyCode currency={paymentCurrency} value={item.tax} />
            </div>
          );
        })}
      </>
    ) : null;
  };

  const getDiscountList = (adjustments: DiscountAdjustments) => {
    const discountItems: discountListItem[] = [];
    adjustments?.forEach((adjustment) => {
      const discountKey = adjustment.type;
      const existingDiscountItem = discountItems.find((discountItem) => discountItem.discountType === discountKey);
      if (existingDiscountItem) {
        existingDiscountItem.discountValue += toNumber(adjustment.amount);
      } else {
        discountItems.push({
          discountType: discountKey,
          discountLabel: DISCOUNT_LABELS[discountKey as keyof typeof DISCOUNT_LABELS] ?? DISCOUNT_LABELS.OTHER,
          discountValue: toNumber(adjustment.amount),
        });
      }
    });

    return discountItems?.length > 0 ? (
      <>
        {discountItems.map((discount: discountListItem, index: number) => {
          return (
            <PriceGridItem
              label={discount.discountLabel}
              value={formatToUSD(discount.discountValue, finalCurrency)}
              key={index}
              customClassValue="sub-discount"
              isChildItem={true}
            />
          );
        })}
      </>
    ) : null;
  };

  const taxRates = getTaxRates(allItems, finalCurrency);

  const discountsList = getDiscountList(allDiscountAdjustments);

  const renderAdminFeeDetails = () =>
    adminFeeDetails && (
      <PriceGridItem
        label="Administration Fee (DST)"
        value={formatToUSD(adminFeeDetails.amount, finalCurrency)}
        helpContent={
          !hideAdminFeeTooltip ? (
            <span data-testid="administration-tax-tooltip" className="tax-tooltip">
              <Tooltip
                content={`${adminFeeDetails.percent}%`}
                position="right"
                tag="span"
                component={renderPrimitiveTooltip('5px')}
              >
                <InfoIcon size="small" label="tax-tooltip-info" />
              </Tooltip>
            </span>
          ) : null
        }
      />
    );

  const renderSubtotal = () => <PriceGridItem label="Subtotal" value={formatToUSD(subTotal, finalCurrency)} />;

  const renderDiscount = () => (
    <PriceGridItem
      label="Total discount"
      value={formatToUSD(discount, finalCurrency)}
      helpContent={
        discountsList && (
          <Button
            className="expandable-discount-button"
            iconBefore={discountArrowToShow}
            appearance="subtle"
            onClick={toggleExpanded}
            testId="expandable-discount-button"
          />
        )
      }
    />
  );

  const renderDiscountBreakup = () => {
    return (
      <div
        className={classNames('price-grid', {
          'collapsed-accordion': !isExpanded,
          'expanded-accordion': isExpanded,
        })}
      >
        {discountsList}
      </div>
    );
  };

  const renderCreditAmount = () => <PriceGridItem label="Credit" value={formatToUSD(credits, finalCurrency)} />;

  const renderProratedChargeAmount = () =>
    shouldShowProratedCharge && (
      <PriceGridItem label="Prorated Charge" value={formatToUSD(getProratedCharge(billEstimate))} />
    );

  const renderTaxDetails = () => (
    <PriceGridItem
      label="Tax"
      value={formatToUSD(taxes ?? 0, finalCurrency)}
      helpContent={
        taxRates && (
          <span data-testid="tax-tooltip" className="tax-tooltip">
            <Tooltip content={taxRates} position="left" tag="span" component={renderPrimitiveTooltip('35px')}>
              <InfoIcon size="small" label="tax-tooltip-info" />
            </Tooltip>
          </span>
        )
      }
    />
  );

  const renderPriceGrid = () => (
    <>
      <div className="price-grid">
        {renderSubtotal()}
        {renderProratedChargeAmount()}
        {renderDiscount()}
      </div>
      {discountsList && renderDiscountBreakup()}
      <div className="price-grid">
        {renderCreditAmount()}
        {renderTaxDetails()}
        {renderAdminFeeDetails()}
      </div>
    </>
  );

  const renderOldAndNewPrice = () => {
    return (
      <>
        <p className="old-price" aria-label="Current grand total of bill estimate">
          {formatToUSD(currentEstimatedBill, finalCurrency)}
        </p>
        <p className="new-price" data-testid="new-price" aria-label="Updated grand total of bill estimate">
          {formatToUSD(grandTotal, finalCurrency)}
        </p>
      </>
    );
  };

  if (isDesignIteration1Enabled) {
    return (
      <Stack space="space.300">
        <Flex justifyContent="space-between">
          <Text weight="medium">Next price estimate</Text>
          <Text weight="bold">{renderOldAndNewPrice()}</Text>
        </Flex>
        <Box>
          <Text weight="bold">Monthly estimate</Text>
          {renderPriceGrid()}
        </Box>
        <Text color="color.text.subtle">Estimates based on anticipated number of users.</Text>
      </Stack>
    );
  }

  return (
    <>
      {renderOldAndNewPrice()}
      {renderPriceGrid()}
    </>
  );
};
